/**
 * 테이블 컬럼 캐시 — 편집기의 컬럼 자동완성이 쓴다(2026-10-03).
 *
 * 스키마 탐색기가 든 것은 테이블·뷰 **이름**뿐이라 컬럼은 여기서 처음 쓸 때 한 번 받아 둔다(연결·스키마·테이블 단위,
 * `dbQueries[dbType].fetchColumnsForTable` — 테이블 정보 탭과 **같은 SQL**). 같은 요청이 겹치면 한 번만 보낸다.
 * `peek` 은 동기(이미 있을 때만), `get` 은 없으면 받아 온다. 실패는 짧게 기억해 같은 테이블로 계속 두드리지 않는다.
 * 연결을 끊거나 스키마를 새로고침하면 `clear(connectionId)`.
 */
import axios from 'axios';
import dbQueries from '../config/dbQueries';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/sql-developer';
const FAIL_TTL_MS = 30 * 1000;

const cache = new Map();       // key → [{ name, type, comment }]
const inflight = new Map();    // key → Promise
const failedAt = new Map();    // key → ms

const keyOf = (connectionId, schema, table) => `${connectionId}|${(schema || '').toUpperCase()}|${String(table).toUpperCase()}`;

function normalizeRows(rows) {
    return (rows || []).map(r => ({
        name: r.NAME ?? r.name ?? r.COLUMN_NAME ?? r.column_name ?? '',
        type: r.TYPE ?? r.type ?? '',
        comment: r.COMMENTS ?? r.comments ?? r.COMMENT ?? r.comment ?? null,
    })).filter(c => c.name);
}

export function peekColumns(connectionId, schema, table) {
    return cache.get(keyOf(connectionId, schema, table)) || null;
}

export async function getColumns({ connectionId, dbType, schema, table, usrid }) {
    if (!connectionId || !table) return null;
    const key = keyOf(connectionId, schema, table);
    const hit = cache.get(key);
    if (hit) return hit;
    const f = failedAt.get(key);
    if (f && Date.now() - f < FAIL_TTL_MS) return null;
    if (inflight.has(key)) return inflight.get(key);
    const q = dbQueries[dbType || 'oracle'];
    if (!q || typeof q.fetchColumnsForTable !== 'function') return null;
    const p = (async () => {
        try {
            const sql = q.fetchColumnsForTable(schema, table);
            const res = await axios.post(`${API_BASE}/execute`, { query: sql, connectionId, ...(usrid ? { usrid } : {}) }, { timeout: 15000 });
            const rows = Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.rows) ? res.data.rows : []);
            const cols = normalizeRows(rows);
            // 0건은 캐시하지 않는다 — 스키마를 잘못 물었을 가능성이 커서, 잠깐 쉬었다가 다시 묻는다(실패와 같은 취급)
            if (cols.length === 0) { failedAt.set(key, Date.now()); return null; }
            cache.set(key, cols);
            return cols;
        } catch (_e) {
            failedAt.set(key, Date.now());
            return null;
        } finally {
            inflight.delete(key);
        }
    })();
    inflight.set(key, p);
    return p;
}

export function clearColumnCache(connectionId) {
    for (const k of [...cache.keys(), ...failedAt.keys()]) {
        if (!connectionId || k.startsWith(`${connectionId}|`)) { cache.delete(k); failedAt.delete(k); }
    }
}
